import React, { useCallback } from 'react';

import styled from 'styled-components';
import BNumberFormat from 'react-number-format';

import { InputProps } from '@components/Input/InputProps';
import { SeturInputProps } from '@components/Input';

import { Container, InputField } from './styled';

const NumberFormat = styled(BNumberFormat)`
  border: none;
  outline: none;
  background: transparent;
`;

const MAX_VAL = 99_999_999.99;

type NumberInputProps = {
  onChange?: <T>(v: T) => void;
  className?: string;
  defaultValue?: string;
  format?: string;
  currency?: string;
  value?: string;
  maxLength?: number;
  getInputRef?: <T>(e: T) => void;
};

const CurrencyConfig = {
  suffix: ' ₺',
  allowNegative: false,
  thousandSeparator: '.',
  decimalSeparator: ',',
  decimalScale: 2,
};
const SeturPayCurrency = {
  ...CurrencyConfig,
  thousandSeparator: null,
  decimalSeparator: false,
};
export const FormattedNumeric = styled(
  ({
    onChange,
    className,
    defaultValue,
    format,
    currency = false,
    getInputRef,
    max,
    value,
    inputMode = 'numeric',
    iSeturPayControl,
  }) => {
    const isAllowed = useCallback(
      ({ floatValue = 0 }) => {
        return floatValue <= (max ?? MAX_VAL);
      },
      [max]
    );
    const currencyProps = iSeturPayControl
      ? SeturPayCurrency
      : currency
      ? CurrencyConfig
      : {};
    return (
      <NumberFormat
        format={format}
        inputMode={inputMode}
        onValueChange={(target: { value: unknown }) => {
          onChange?.({ target });
        }}
        className={className}
        defaultValue={defaultValue}
        getInputRef={getInputRef}
        {...currencyProps}
        isAllowed={isAllowed}
        value={value}
        isNumericString={true}
      />
    );
  }
)<NumberInputProps>`
  & + *[data-role='label'] {
    font-size: 12px !important;
    padding-bottom: 10px;
  }
`;

type NumericProps = {
  showInput?: boolean;
  format?: string | ((value: string) => string);
  mode?: 'active' | 'inactive';
  getInputRef?: unknown;
  innerInputDisabled?: boolean;
  currency?: boolean;
  maxValue?: number;
  iSeturPayControl?: boolean;
};

export type InputNumericProps = SeturInputProps & InputProps & NumericProps;

export const InputNumeric = ({
  children,
  width,
  icon,
  suffix,
  showInput = true,
  innerInputDisabled = false,
  disabled = false,
  mode = 'active',
  ...rest
}: React.PropsWithChildren<InputNumericProps>) => {
  return (
    <Container
      width={width}
      icon={icon}
      suffix={suffix}
      inputShown={showInput}
      disabled={disabled}
    >
      {showInput ? (
        <InputField
          width='100%'
          {...rest}
          disabled={innerInputDisabled || disabled}
          InputComponent={FormattedNumeric}
          mode={mode}
        />
      ) : null}
      {children}
    </Container>
  );
};

export default InputNumeric;
